import axios from 'axios';

// Интерфейс для метаданных персонажа в формате Metaplex
export interface CharacterMetadata {
  name: string;
  symbol: string;
  description: string;
  image: string;
  external_url: string;
  seller_fee_basis_points: number;
  attributes: Array<{ trait_type: string; value: any }>;
  collection: {
    name: string;
    family: string;
  };
  properties: {
    files: Array<{
      uri: string;
      type: string;
      cdn?: boolean;
    }>;
    category: string;
    creators: Array<{
      address: string;
      share: number;
    }>;
    model: string;
  };
}

// Функция для загрузки метаданных через серверный API на Cloudflare
export async function uploadMetadataToCloudflare(
  metadata: CharacterMetadata, 
  walletAddress: string
): Promise<{ 
  success: boolean; 
  metadataId?: string; 
  metadataUrl?: string; 
  error?: string 
}> {
  try {
    console.log('Загрузка метаданных на Cloudflare...');
    
    // Анализируем размер метаданных перед отправкой
    const metadataStr = JSON.stringify(metadata);
    const metadataSize = new TextEncoder().encode(metadataStr).length;
    const metadataSizeKB = Math.round(metadataSize / 1024);
    
    console.log(`Размер метаданных: ${metadataSizeKB} КБ`);
    
    // Проверяем размер изображений и оптимизируем если нужно
    try {
      // Анализируем основное изображение
      if (metadata.image && typeof metadata.image === 'string' && metadata.image.startsWith('data:image')) {
        const imageSize = metadata.image.length;
        const imageSizeKB = Math.round(imageSize / 1024);
        console.log(`Размер основного изображения: ${imageSizeKB} КБ`);
        
        // Если изображение слишком большое, заменяем его
        if (imageSizeKB > 300) {
          console.log(`Изображение слишком большое (${imageSizeKB} КБ), заменяем его`);
          metadata.image = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";
        }
      }
      
      // Анализируем файлы в properties
      if (metadata.properties && metadata.properties.files) {
        for (let i = 0; i < metadata.properties.files.length; i++) {
          const file = metadata.properties.files[i];
          if (file.uri && typeof file.uri === 'string' && file.uri.startsWith('data:image')) {
            const fileSize = file.uri.length;
            const fileSizeKB = Math.round(fileSize / 1024);
            console.log(`Размер файла ${i}: ${fileSizeKB} КБ`);
            
            // Если файл слишком большой, заменяем его
            if (fileSizeKB > 300) {
              console.log(`Файл ${i} слишком большой (${fileSizeKB} КБ), заменяем его`);
              file.uri = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";
            }
          }
        }
      }
    } catch (optimizeError) {
      console.error('Ошибка при оптимизации изображений:', optimizeError);
      // Продолжаем с оригинальными данными
    }
    
    // Сначала попробуем отправить данные на логгер для диагностики
    try {
      await axios.post('/api/request-logger', {
        metadata,
        walletAddress
      });
    } catch (logError) {
      console.error('Ошибка при логировании запроса:', logError);
      // Игнорируем ошибку логирования и продолжаем
    }
    
    // Отправляем запрос на сохранение метаданных
    const response = await axios.post('/api/metadata', {
      metadata,
      walletAddress
    });

    if (response.data.success) {
      return {
        success: true,
        metadataId: response.data.metadataId,
        metadataUrl: response.data.metadataUrl
      };
    } else {
      return {
        success: false,
        error: response.data.error || 'Неизвестная ошибка при загрузке метаданных'
      };
    }
  } catch (error) {
    console.error('Ошибка загрузки метаданных:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Сетевая ошибка при загрузке метаданных'
    };
  }
}

// Функция для чеканки SFT на основе метаданных
export async function mintSFT(
  metadataId: string, 
  walletAddress: string
): Promise<{ 
  success: boolean; 
  mintAddress?: string; 
  txId?: string; 
  error?: string 
}> {
  try {
    console.log('Чеканка SFT с использованием метаданных...');
    const response = await axios.post('/api/mint-sft', {
      metadataId,
      walletAddress
    });

    if (response.data.success) {
      return {
        success: true,
        mintAddress: response.data.mintAddress,
        txId: response.data.txId
      };
    } else {
      return {
        success: false,
        error: response.data.error || 'Ошибка при чеканке SFT'
      };
    }
  } catch (error) {
    console.error('Ошибка чеканки SFT:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Сетевая ошибка при чеканке SFT'
    };
  }
} 